'use client'

import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { toast } from '@/components/ui/toast'
import { subscribeDataState } from '@/lib/api-client'

/** Many writes can fail together during a reset; one toast per window is enough. */
const MAINTENANCE_TOAST_GAP_MS = 15_000

/**
 * Shell-wide view of the baseline reset signals the API client sees (plan 08
 * N2): a toast when a write is refused while data is refreshed, and a reload
 * prompt once the data generation has moved on from the one this tab loaded
 * with. Silent until the backend sends `X-Data-Generation` or a 503 with
 * `Retry-After`.
 */
export function DataStateWatcher() {
  const [changed, setChanged] = useState(false)
  const lastMaintenanceToast = useRef(0)

  useEffect(
    () =>
      subscribeDataState((event) => {
        if (event.type === 'generation-changed') {
          setChanged(true)
          return
        }
        const now = Date.now()
        if (now - lastMaintenanceToast.current < MAINTENANCE_TOAST_GAP_MS) return
        lastMaintenanceToast.current = now
        toast.warning(event.message)
      }),
    []
  )

  if (!changed) return null

  return (
    <div
      role='status'
      aria-live='polite'
      className='fixed inset-x-4 bottom-4 z-toast mx-auto flex max-w-xl flex-wrap items-center gap-3 rounded-2xl border border-hairline bg-card p-4 shadow-float sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2'
    >
      <Icons.refresh className='h-5 w-5 shrink-0 text-brand-purple' strokeWidth={1.5} aria-hidden='true' />
      <p className='min-w-0 flex-1 text-sm text-foreground'>
        The data was refreshed. Reload to see the latest.
      </p>
      <div className='flex gap-2'>
        <Button size='sm' variant='outline' onClick={() => setChanged(false)}>
          Later
        </Button>
        <Button size='sm' onClick={() => window.location.reload()}>
          Reload
        </Button>
      </div>
    </div>
  )
}
